/**
 * The employee's own access log on the wire — *"who has looked at my data?"*
 * (Tier-3 U9, `openapi.yaml`'s `SelfAccessLogEntry`).
 *
 * Hand-written to mirror the contract, exactly as `dsar.ts` and `geo.ts` describe for
 * their slices: transcribe what the screen reads, not the whole document.
 *
 * ── There is no `employee_id` anywhere in this file ─────────────────────────
 *
 * The same shape `dsar.ts` keeps, and for the same reason: the server resolves the
 * subject from the session, so there is no argument position on any function here
 * that could name a person. `accessLogPath` takes a cursor and nothing else.
 *
 * ── The cursor is opaque and this file treats it as opaque ──────────────────
 *
 * It is a position in an ordering the server owns. Nothing here parses it, compares
 * it or constructs one — a client that did would be depending on a sort key the
 * contract deliberately does not publish. It is read from `page.next_cursor` and
 * handed straight back.
 */

/** Whether a row named the caller, or named nobody because it covered everyone. */
export type AccessLogScope = 'self' | 'company';

/**
 * One row. Six facts, and the absences are the contract's own: no `record_count`,
 * no `resource_id`, no actor id, no IP — a `company` row is shown to every employee
 * of the company, so nothing on this wire may describe another individual.
 */
export interface AccessLogEntry {
  readonly id: string;
  readonly occurred_at: string;
  readonly actor_label: string;
  /** `view`, `list`, `export`, `download`, `ai_query`. Not a closed set on the wire. */
  readonly access_type: string;
  /** `employee_attendance`, `report`, `dsar_export`, … Not a closed set on the wire. */
  readonly resource_type: string;
  readonly reason: string | null;
  readonly scope: AccessLogScope;
}

export interface AccessLogPage {
  readonly data: readonly AccessLogEntry[];
  readonly page: {
    readonly next_cursor: string | null;
    readonly has_more: boolean;
  };
}

export const SELF_ACCESS_LOG_PATH = '/api/v1/audit/my-access-log';

/**
 * How many rows a page carries.
 *
 * Ten, not the endpoint's 200: this is a region below the export region on a screen
 * whose primary job is the export, and a hundred rows would push everything else off
 * the page. The rest is one press away.
 */
export const ACCESS_LOG_PAGE_SIZE = 10;

/** One page, from the start or from where the last one ended. */
export function accessLogPath(cursor: string | null = null): string {
  const query = new URLSearchParams({ limit: String(ACCESS_LOG_PAGE_SIZE) });
  if (cursor !== null) query.set('cursor', cursor);
  return `${SELF_ACCESS_LOG_PATH}?${query.toString()}`;
}
